import { useEffect, useMemo, useState } from "react"
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Paper,
  Snackbar,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography,
} from "@mui/material"

import MainLayout from "../../layouts/MainLayout"

import {
  activateSupplier,
  createSupplier,
  deactivateSupplier,
  deleteSupplier,
  getSupplierById,
  getSuppliers,
  updateSupplier,
} from "../../services/supplierService"

const defaultFormData = {
  name: "",
  organization_id: 1,
  supplier_type: "company",
  contact_person: "",
  phone: "",
  email: "",
  address: "",
  city: "",
  province: "",
  country: "République démocratique du Congo",
  tax_number: "",
  payment_terms_days: 30,
  default_currency: "USD",
  exchange_rate: 1,
  notes: "",
  is_active: true,
}

const supplierTypes = [
  { value: "company", label: "Entreprise" },
  { value: "individual", label: "Particulier" },
  { value: "manufacturer", label: "Fabricant" },
  { value: "distributor", label: "Distributeur" },
]


function MetricCard({
  label,
  value,
  caption,
  accent = "#2563eb",
}) {
  return (
    <Paper
      variant="outlined"
      sx={{
        p: 2,
        minWidth: 0,
        minHeight: 100,
        borderRadius: 3.5,
        borderLeft: "4px solid",
        borderLeftColor: accent,
        boxShadow: "0 8px 24px rgba(15, 23, 42, 0.04)",
      }}
    >
      <Typography
        variant="caption"
        sx={{
          color: "text.secondary",
          fontWeight: 800,
          letterSpacing: 0.25,
        }}
      >
        {label}
      </Typography>

      <Typography
        sx={{
          mt: 0.35,
          fontSize: { xs: 24, md: 28 },
          lineHeight: 1.15,
          fontWeight: 900,
          color: "#0f172a",
        }}
      >
        {value}
      </Typography>

      {caption ? (
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ display: "block", mt: 0.6 }}
        >
          {caption}
        </Typography>
      ) : null}
    </Paper>
  )
}

function SuppliersPage() {
  const [suppliers, setSuppliers] = useState([])
  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)

  const [open, setOpen] = useState(false)
  const [selectedSupplier, setSelectedSupplier] = useState(null)
  const [formData, setFormData] = useState(defaultFormData)

  const [searchTerm, setSearchTerm] = useState("")

  const [page, setPage] = useState(0)
  const rowsPerPage = 5
  const [selectedType, setSelectedType] = useState("")
  const [selectedStatus, setSelectedStatus] = useState("")

  useEffect(() => {
    setPage(0)
  }, [searchTerm, selectedType, selectedStatus])

  const [snackbar, setSnackbar] = useState({
    open: false,
    severity: "success",
    message: "",
  })

  const showSnackbar = (message, severity = "success") => {
    setSnackbar({
      open: true,
      severity,
      message,
    })
  }

  const loadSuppliers = async () => {
    try {
      setLoading(true)
      const data = await getSuppliers()
      setSuppliers(Array.isArray(data) ? data : [])
    } catch (error) {
      console.error(error)
      showSnackbar("Impossible de charger les fournisseurs.", "error")
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    loadSuppliers()
  }, [])

  const resetForm = () => {
    setSelectedSupplier(null)
    setFormData(defaultFormData)
  }

  const openAddDialog = () => {
    resetForm()
    setOpen(true)
  }

  const openEditDialog = async (supplierId) => {
    try {
      const supplier = await getSupplierById(supplierId)

      setSelectedSupplier(supplier)

      setFormData({
        name: supplier.name || "",
        organization_id: Number(supplier.organization_id || 1),
        supplier_type: supplier.supplier_type || "company",
        contact_person: supplier.contact_person || "",
        phone: supplier.phone || "",
        email: supplier.email || "",
        address: supplier.address || "",
        city: supplier.city || "",
        province: supplier.province || "",
        country:
          supplier.country || "République démocratique du Congo",
        tax_number: supplier.tax_number || "",
        payment_terms_days: Number(supplier.payment_terms_days || 0),
        default_currency: supplier.default_currency || "USD",
        exchange_rate: Number(supplier.exchange_rate || 1),
        notes: supplier.notes || "",
        is_active:
          typeof supplier.is_active === "boolean"
            ? supplier.is_active
            : true,
      })

      setOpen(true)
    } catch (error) {
      console.error(error)
      showSnackbar("Impossible de charger le fournisseur.", "error")
    }
  }

  const handleClose = () => {
    if (saving) return

    setOpen(false)
    resetForm()
  }

  const handleChange = (event) => {
    const { name, value } = event.target

    setFormData((prev) => ({
      ...prev,
      [name]:
        name === "is_active"
          ? value === "true"
          : [
              "organization_id",
              "payment_terms_days",
              "exchange_rate",
            ].includes(name)
          ? Number(value)
          : value,
    }))
  }

  const validateForm = () => {
    if (!formData.name.trim()) {
      showSnackbar("Le nom du fournisseur est obligatoire.", "warning")
      return false
    }

    if (!formData.supplier_type) {
      showSnackbar("Le type de fournisseur est obligatoire.", "warning")
      return false
    }

    return true
  }

  const handleSave = async () => {
    if (!validateForm()) return

    const payload = {
      name: formData.name.trim(),
      organization_id: Number(formData.organization_id || 1),
      supplier_type: formData.supplier_type,
      contact_person: formData.contact_person?.trim() || null,
      phone: formData.phone?.trim() || null,
      email: formData.email?.trim() || null,
      address: formData.address?.trim() || null,
      city: formData.city?.trim() || null,
      province: formData.province?.trim() || null,
      country:
        formData.country?.trim() ||
        "République démocratique du Congo",
      tax_number: formData.tax_number?.trim() || null,
      payment_terms_days: Number(formData.payment_terms_days || 0),
      default_currency: formData.default_currency || "USD",
      exchange_rate: Number(formData.exchange_rate || 1),
      notes: formData.notes?.trim() || null,
      is_active: Boolean(formData.is_active),
    }

    try {
      setSaving(true)

      if (selectedSupplier?.id) {
        await updateSupplier(selectedSupplier.id, payload)
        showSnackbar("Fournisseur modifié avec succès.")
      } else {
        await createSupplier(payload)
        showSnackbar("Fournisseur créé avec succès.")
      }

      setOpen(false)
      resetForm()
      await loadSuppliers()
    } catch (error) {
      console.error(error)

      showSnackbar(
        error?.response?.data?.detail ||
          "Impossible d'enregistrer le fournisseur.",
        "error"
      )
    } finally {
      setSaving(false)
    }
  }

  const handleDelete = async (supplierId) => {
    if (
      !window.confirm(
        "Voulez-vous réellement supprimer ce fournisseur ?"
      )
    ) {
      return
    }

    try {
      await deleteSupplier(supplierId)
      showSnackbar("Fournisseur supprimé.")
      await loadSuppliers()
    } catch (error) {
      console.error(error)

      showSnackbar(
        error?.response?.data?.detail ||
          "Impossible de supprimer ce fournisseur.",
        "error"
      )
    }
  }

  const handleToggleStatus = async (supplier) => {
    try {
      if (supplier.is_active) {
        await deactivateSupplier(supplier.id)
        showSnackbar("Fournisseur désactivé.")
      } else {
        await activateSupplier(supplier.id)
        showSnackbar("Fournisseur activé.")
      }

      await loadSuppliers()
    } catch (error) {
      console.error(error)

      showSnackbar(
        error?.response?.data?.detail ||
          "Impossible de modifier le statut.",
        "error"
      )
    }
  }

  const filteredSuppliers = useMemo(() => {
    return suppliers.filter((supplier) => {
      const q = searchTerm.trim().toLowerCase()

      const matchesSearch =
        !q ||
        supplier.name?.toLowerCase().includes(q) ||
        supplier.contact_person?.toLowerCase().includes(q) ||
        supplier.phone?.toLowerCase().includes(q) ||
        supplier.email?.toLowerCase().includes(q) ||
        supplier.city?.toLowerCase().includes(q)

      const matchesType =
        !selectedType || supplier.supplier_type === selectedType

      const matchesStatus =
        selectedStatus === ""
          ? true
          : selectedStatus === "active"
          ? supplier.is_active
          : !supplier.is_active

      return matchesSearch && matchesType && matchesStatus
    })
  }, [suppliers, searchTerm, selectedType, selectedStatus])

  const resetFilters = () => {
    setSearchTerm("")
    setSelectedType("")
    setSelectedStatus("")
  }

  return (
    <MainLayout>
      <Box
        sx={{
          minWidth: 0,
          pb: { xs: 2, md: 4 },
          "& .MuiPaper-root": {
            borderColor: "rgba(148, 163, 184, 0.24)",
            backgroundImage: "none",
          },
          "& .MuiButton-root": {
            textTransform: "none",
            fontWeight: 800,
            borderRadius: 2.5,
          },
          "& .MuiTextField-root .MuiOutlinedInput-root": {
            borderRadius: 2.5,
          },
          "& .MuiAlert-root": {
            borderRadius: 3,
          },
          "& .MuiTableHead-root .MuiTableCell-root": {
            backgroundColor: "#f8fafc",
            color: "#334155",
            fontWeight: 800,
            whiteSpace: "nowrap",
          },
          "& .MuiTableBody-root .MuiTableRow-root:hover": {
            backgroundColor: "#f8fbff",
          },
          "& .MuiChip-root": {
            fontWeight: 700,
          },
        }}
      >
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: {
              xs: "1fr",
              md: "minmax(0, 1fr) auto",
            },
            alignItems: "start",
            columnGap: 2,
            rowGap: 1.25,
            mb: 2.5,
            width: "100%",
          }}
        >
          <Box sx={{ minWidth: 0 }}>
            <Typography
              variant="h4"
              fontWeight={900}
              sx={{
                color: "#0f172a",
                fontSize: {
                  xs: "1.65rem",
                  sm: "1.95rem",
                  md: "2.15rem",
                },
                lineHeight: 1.12,
                letterSpacing: "-0.02em",
              }}
            >
              Fournisseurs
            </Typography>

            <Typography
              variant="body2"
              color="text.secondary"
              sx={{
                mt: 0.5,
                maxWidth: 760,
                lineHeight: 1.55,
              }}
            >
              Gestion des fournisseurs, coordonnées et conditions commerciales.
            </Typography>
          </Box>

          <Box
            sx={{
              display: "flex",
              justifyContent: {
                xs: "stretch",
                md: "flex-end",
              },
              justifySelf: {
                xs: "stretch",
                md: "end",
              },
              width: {
                xs: "100%",
                md: "auto",
              },
              pt: {
                xs: 0.5,
                md: 0.25,
              },
            }}
          >
            <Button
              variant="contained"
              onClick={openAddDialog}
              sx={{
                width: {
                  xs: "100%",
                  sm: "auto",
                },
                minHeight: 42,
                px: 2.25,
                borderRadius: 2.5,
                whiteSpace: "nowrap",
                boxShadow: "0 8px 20px rgba(37, 99, 235, 0.18)",
              }}
            >
              Nouveau fournisseur
            </Button>
          </Box>
        </Box>


        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: {
              xs: "1fr",
              sm: "repeat(2, minmax(0, 1fr))",
              lg: "repeat(4, minmax(0, 1fr))",
            },
            gap: 1.5,
            mb: 2,
          }}
        >
          <MetricCard
            label="Fournisseurs"
            value={suppliers.length}
            caption="Total enregistré"
          />
          <MetricCard
            label="Actifs"
            value={
              suppliers.filter(
                (supplier) => Boolean(supplier.is_active)
              ).length
            }
            caption="Disponibles pour les achats"
            accent="#16a34a"
          />
          <MetricCard
            label="Inactifs"
            value={
              suppliers.filter(
                (supplier) => !supplier.is_active
              ).length
            }
            caption="Temporairement désactivés"
            accent="#64748b"
          />
          <MetricCard
            label="Entreprises"
            value={
              suppliers.filter(
                (supplier) =>
                  supplier.supplier_type === "company"
              ).length
            }
            caption="Fournisseurs de type entreprise"
            accent="#0891b2"
          />
        </Box>



        <Paper sx={{ p: 2, mb: 2, borderRadius: 2 }}>
          <Stack
            direction={{ xs: "column", md: "row" }}
            spacing={2}
          >
            <TextField
              fullWidth
              placeholder="Rechercher fournisseur, téléphone, ville..."
              value={searchTerm}
              onChange={(event) => setSearchTerm(event.target.value)}
            />

            <TextField
              select
              value={selectedType}
              onChange={(event) => setSelectedType(event.target.value)}
              sx={{ minWidth: { xs: "100%", md: 190 } }}
            >
              <MenuItem value="">Tous les types</MenuItem>

              {supplierTypes.map((type) => (
                <MenuItem key={type.value} value={type.value}>
                  {type.label}
                </MenuItem>
              ))}
            </TextField>

            <TextField
              select
              value={selectedStatus}
              onChange={(event) =>
                setSelectedStatus(event.target.value)
              }
              sx={{ minWidth: { xs: "100%", md: 170 } }}
            >
              <MenuItem value="">Tous statuts</MenuItem>
              <MenuItem value="active">Actif</MenuItem>
              <MenuItem value="inactive">Inactif</MenuItem>
            </TextField>

            <Button variant="outlined" onClick={resetFilters}>
              Reset
            </Button>
          </Stack>
        </Paper>

        <Paper sx={{ borderRadius: 3.5, overflow: "hidden" }}>
          <Box sx={{ width: "100%", overflowX: "auto" }}>
            <Table sx={{ minWidth: 1200 }}>
              <TableHead>
                <TableRow>
                  <TableCell>Fournisseur</TableCell>
                  <TableCell>Type</TableCell>
                  <TableCell>Contact</TableCell>
                  <TableCell>Téléphone</TableCell>
                  <TableCell>Ville</TableCell>
                  <TableCell>Délai paiement</TableCell>
                  <TableCell>Devise</TableCell>
                  <TableCell>Statut</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>

              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={9}>
                      Chargement...
                    </TableCell>
                  </TableRow>
                ) : filteredSuppliers.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={9}>
                      Aucun fournisseur trouvé.
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredSuppliers
                    .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                    .map((supplier) => (
                    <TableRow key={supplier.id} hover>
                      <TableCell>
                        <Typography fontWeight={600}>
                          {supplier.name}
                        </Typography>

                        <Typography
                          variant="caption"
                          color="text.secondary"
                        >
                          {supplier.email || "-"}
                        </Typography>
                      </TableCell>

                      <TableCell>
                        <Chip
                          size="small"
                          variant="outlined"
                          color="primary"
                          label={supplierTypes.find((type) => type.value === supplier.supplier_type)?.label || supplier.supplier_type || "-"}
                        />
                      </TableCell>

                      <TableCell>
                        {supplier.contact_person || "-"}
                      </TableCell>

                      <TableCell>{supplier.phone || "-"}</TableCell>

                      <TableCell>{supplier.city || "-"}</TableCell>

                      <TableCell>
                        {Number(
                          supplier.payment_terms_days || 0
                        )}{" "}
                        jour(s)
                      </TableCell>

                      <TableCell>
                        {supplier.default_currency || "USD"}
                      </TableCell>

                      <TableCell>
                        <Chip
                          size="small"
                          color={
                            supplier.is_active
                              ? "success"
                              : "default"
                          }
                          label={
                            supplier.is_active ? "Actif" : "Inactif"
                          }
                        />
                      </TableCell>

                      <TableCell>
                        <Stack direction="row" spacing={1}>
                          <Button
                            size="small"
                            variant="contained"
                            onClick={() =>
                              openEditDialog(supplier.id)
                            }
                          >
                            Modifier
                          </Button>

                          <Button
                            size="small"
                            variant="outlined"
                            onClick={() =>
                              handleToggleStatus(supplier)
                            }
                          >
                            {supplier.is_active
                              ? "Désactiver"
                              : "Activer"}
                          </Button>

                          <Button
                            size="small"
                            variant="outlined"
                            color="error"
                            onClick={() =>
                              handleDelete(supplier.id)
                            }
                          >
                            Supprimer
                          </Button>
                        </Stack>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </Box>
        

          <TablePagination
            component="div"
            count={filteredSuppliers.length}
            page={page}
            onPageChange={(_, newPage) => setPage(newPage)}
            rowsPerPage={rowsPerPage}
            rowsPerPageOptions={[5]}
            labelRowsPerPage="Lignes"
            labelDisplayedRows={({ from, to, count }) => `${from}–${to} sur ${count}`}
            sx={{
              borderTop: "1px solid",
              borderColor: "divider",
              "& .MuiTablePagination-toolbar": {
                minHeight: 54,
                px: { xs: 1, sm: 2 },
              },
            }}
          />
        </Paper>
      </Box>

      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          {selectedSupplier
            ? "Modifier fournisseur"
            : "Nouveau fournisseur"}
        </DialogTitle>

        <DialogContent>
          <Stack spacing={2} mt={1}>
            <Stack
              direction={{ xs: "column", md: "row" }}
              spacing={2}
            >
              <TextField
                label="Nom du fournisseur"
                name="name"
                value={formData.name}
                onChange={handleChange}
                fullWidth
                required
              />

              <TextField
                select
                label="Type"
                name="supplier_type"
                value={formData.supplier_type}
                onChange={handleChange}
                fullWidth
              >
                {supplierTypes.map((type) => (
                  <MenuItem key={type.value} value={type.value}>
                    {type.label}
                  </MenuItem>
                ))}
              </TextField>
            </Stack>

            <Stack
              direction={{ xs: "column", md: "row" }}
              spacing={2}
            >
              <TextField
                label="Personne de contact"
                name="contact_person"
                value={formData.contact_person}
                onChange={handleChange}
                fullWidth
              />

              <TextField
                label="Téléphone"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                fullWidth
              />

              <TextField
                label="Email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                fullWidth
              />
            </Stack>

            <TextField
              label="Adresse"
              name="address"
              value={formData.address}
              onChange={handleChange}
              fullWidth
            />

            <Stack
              direction={{ xs: "column", md: "row" }}
              spacing={2}
            >
              <TextField
                label="Ville"
                name="city"
                value={formData.city}
                onChange={handleChange}
                fullWidth
              />

              <TextField
                label="Province"
                name="province"
                value={formData.province}
                onChange={handleChange}
                fullWidth
              />

              <TextField
                label="Pays"
                name="country"
                value={formData.country}
                onChange={handleChange}
                fullWidth
              />
            </Stack>

            <Stack
              direction={{ xs: "column", md: "row" }}
              spacing={2}
            >
              <TextField
                label="Numéro fiscal"
                name="tax_number"
                value={formData.tax_number}
                onChange={handleChange}
                fullWidth
              />

              <TextField
                type="number"
                label="Délai de paiement (jours)"
                name="payment_terms_days"
                value={formData.payment_terms_days}
                onChange={handleChange}
                fullWidth
              />
            </Stack>

            <Stack
              direction={{ xs: "column", md: "row" }}
              spacing={2}
            >
              <TextField
                select
                label="Devise"
                name="default_currency"
                value={formData.default_currency}
                onChange={handleChange}
                fullWidth
              >
                <MenuItem value="USD">USD</MenuItem>
                <MenuItem value="CDF">CDF</MenuItem>
              </TextField>

              <TextField
                type="number"
                label="Taux de change"
                name="exchange_rate"
                value={formData.exchange_rate}
                onChange={handleChange}
                fullWidth
              />

              <TextField
                select
                label="Statut"
                name="is_active"
                value={String(formData.is_active)}
                onChange={handleChange}
                fullWidth
              >
                <MenuItem value="true">Actif</MenuItem>
                <MenuItem value="false">Inactif</MenuItem>
              </TextField>
            </Stack>

            <TextField
              multiline
              minRows={3}
              label="Notes"
              name="notes"
              value={formData.notes}
              onChange={handleChange}
              fullWidth
            />
          </Stack>
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={handleClose} disabled={saving}>
            Annuler
          </Button>

          <Button
            variant="contained"
            onClick={handleSave}
            disabled={saving}
          >
            {saving
              ? "Enregistrement..."
              : selectedSupplier
              ? "Modifier"
              : "Enregistrer"}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3500}
        onClose={() =>
          setSnackbar((prev) => ({ ...prev, open: false }))
        }
        anchorOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
      >
        <Alert
          severity={snackbar.severity}
          variant="filled"
          onClose={() =>
            setSnackbar((prev) => ({ ...prev, open: false }))
          }
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </MainLayout>
  )
}

export default SuppliersPage